import { DateTime } from 'luxon';
import i18n from '@/i18n';
import Vue from 'vue';
import { state as authState } from '../store/auth/auth';

/**
 * Get the order status label based on the status
 *
 * @param {string} status
 * @return {string}
 */
export function getOrderStatus(status: string): string {
	const statusMapping: { [key: string]: string } = {
		'pending': i18n.t('orders.management.parent_card.order_card.pending'),
		'in-progress': i18n.t('orders.management.parent_card.order_card.in_progress'),
		'completed': i18n.t('orders.management.parent_card.order_card.completed'),
		'cancelled': i18n.t('orders.management.parent_card.order_card.cancelled'),
		'refunded': i18n.t('orders.management.parent_card.order_card.refunded'),
		'refund-requested': i18n.t('orders.management.parent_card.order_card.refund_requested')
	};

	return statusMapping[status];
}

/**
 * Get the order status color based on the status
 *
 * @param {string} status
 * @return {string}
 */
export function getOrderColorStatus(status: string): string {
	const statusMapping: { [key: string]: string } = {
		'pending': 'status-pending',
		'in-progress': 'status-in-progress',
		'completed': 'status-completed',
		'cancelled': 'status-cancelled',
		'refunded': 'status-refunded',
		'refund-requested': 'status-refunded'
	};

	return statusMapping[status];
}

/**
 * Get the order type value that will be used by the i18n path and the icon to use
 *
 * @param {Order | undefined} order
 * @return {ParentOrderType}
 */
export function getOrderType(order: Order | EventSuiteOrder | undefined): ParentOrderType {
	if(order) {
		if(order.type) {
			if(order.type === 'catering') {
				return { type: 'catering', icon: 'mdi-stadium-outline' };
			}
			else if (order.type === 'generic-catering') {
				if (order.data.checkout.cateringEvent) {
					return { type: 'catering_event', icon: 'mdi-calendar-month-outline' };
				}
				else if (order.data.checkout.pickup?.delivery) {
					return { type: 'delivery', icon: 'mdi-truck-outline' };
				}
				return { type: 'takeout', icon: 'mdi-food-takeout-box-outline' };
			}
			else if(order.type === 'take-out') {
				if(order.data.checkout.pickup?.delivery) {
					return { type: 'delivery', icon: 'mdi-truck-outline' };
				}
				return { type: 'takeout', icon: 'mdi-food-takeout-box-outline' };
			}
			else if(order.type === 'notification') {
				return { type: 'notification', icon: 'mdi-bell-outline' };
			}
			else if(order.type === 'school') {
				return { type: 'school', icon: 'mdi-school' };
			}
			else {
				return { type: 'dine_in', icon: 'mdi-silverware' };
			}
		}
	}
	return { type: 'dine_in', icon: 'mdi-silverware' };
}

/**
 * Get the order title depending of its type
 *
 * @param {Order | undefined} order
 * @return {string}
 */
export function getOrderTitle(order: Order | undefined): string {
	if (order?.type) {
		if (order.type === 'catering' || order.data.suites?.eventDayOrder) {
			return order.data.suites?.eventName || '';
		}
		else if (order.type === 'take-out' || order.type === 'school') {
			if (order.data.checkout.pickup?.delivery) {
				return Vue.filter('localize')(order.data.checkout.delivery, 'address');
			}
			return order.data.checkout.contact?.full_name || '';
		}
		else if (order.type === 'generic-catering' && order.data.checkout.cateringEvent) {
			return Vue.filter('localize')(order.data.checkout.cateringEvent, 'name');
		}
		else if (order.type === 'generic-catering' || order.type === 'notification') {
			return order.data.checkout.contact?.full_name || '';
		}
		return 'Table ' + order.data.config.tableNum;
	}
	return '';
}

/**
 * Get the order's scheduled time depending of its type
 *
 * @param {Order | EventSuiteOrder} order
 * @return {string}
 */
export function getOrderTime(order: Order | EventSuiteOrder): string {
	if (order.pickup_time) {
		return order.pickup_time;
	}
	if (!order.data.suites?.eventDayOrder && order.data.suites?.eventStartDate) {
		return order.data.suites.eventStartDate;
	}
	return order.updated_at;
}

/**
 * Get the order title depending of its type
 *
 * @param {Order | undefined} order
 * @return {{ text: string; color: string }}
 */
export function getOrderTimeLeft(order: Order | undefined): { text: string; color: string } {
	if (!order) {
		return { text: '', color: '' };
	}

	const orderTime: DateTime = DateTime.fromISO(getOrderTime(order));
	const { weeks, days, hours, minutes } = orderTime.diffNow(['weeks', 'days', 'hours', 'minutes']).toObject();

	const timingUnits = [
		{ unit: weeks, name: 'week' },
		{ unit: days, name: 'day' },
		{ unit: hours, name: 'hour' },
		{ unit: minutes, name: 'minute' }
	];

	for (const { unit, name } of timingUnits) {
		if (unit) {
			const absoluteValue = Math.round(Math.abs(unit));
			const isLate = unit < 0;
			const timePath = `orders.management.parent_card.order_card.time_difference.${name}_${isLate ? 'late' : 'left'}`;
			const color = isLate
				? 'late-order'
				: (name === 'minute' && absoluteValue <= 15) ? 'soon-order' : 'early-order';

			return {
				text: i18n.tc(timePath, absoluteValue, { n: absoluteValue }),
				color: color
			};
		}
	}

	return {
		text: i18n.t('orders.management.parent_card.order_card.time_difference.minute_left', { n: 0 }),
		color: 'soon-order'
	};
}

/**
 * Get the order scheduled date. Here we either use the pickup_time or the updated_at
 * if the pickup_time is not set. We then compare the date with the current date
 * and send the according format.
 *
 * @param {Order | EventSuiteOrder | undefined} order
 * @return {string | undefined}
 */
export function getOrderScheduledDate(order: Order | EventSuiteOrder | undefined): string | undefined {
	if(order) {
		const timezone = authState.restaurant.menu_configuration?.zone || 'America/Toronto';
		const date: DateTime = DateTime.fromISO(getOrderTime(order)).setZone(timezone);
		const now: DateTime = DateTime.local().setZone(timezone);
		const timeFormatLocalized: string = i18n.t('orders.management.parent_card.dates.time_format');
		if (date.hasSame(now.minus({ days: 1 }), 'day')) {
			return i18n.t('orders.management.parent_card.dates.yesterday', { time: date.toFormat(timeFormatLocalized) });
		}
		else if (date.hasSame(now, 'day')) {
			return i18n.t('orders.management.parent_card.dates.today', { time: date.toFormat(timeFormatLocalized) });
		}
		else if (date.hasSame(now.plus({ days: 1 }), 'day')) {
			return i18n.t('orders.management.parent_card.dates.tomorrow', { time: date.toFormat(timeFormatLocalized) });
		}
		else {
			return i18n.t('orders.management.parent_card.dates.default', { date: date.toFormat(i18n.t('orders.management.parent_card.dates.scheduled_default_date_format')), time: date.toFormat(timeFormatLocalized) });
		}
	}

	return;
}

/**
 * Get the order delivery location depending of its type
 *
 * @param {Order | EventSuiteOrder | undefined} order
 * @return {string}
 */
export function getOrderDelivery(order: Order | EventSuiteOrder | undefined): string | undefined {
	if (order?.type) {
		if (order.data.suites?.eventDayOrder || order.type === 'catering') {
			return order.data.suites?.eventSuiteName || '';
		}
		else if (['take-out', 'generic-catering', 'school'].includes(order.type)) {
			if (order.data.checkout.cateringEvent?.address) {
				return order.data.checkout.cateringEvent.address;
			}
			else if (order.data.checkout.pickup?.delivery) {
				return Vue.filter('localize')(order.data.checkout.delivery, 'address');
			}
		}
		else if (order.type === 'dine-in') {
			return 'Table ' + order.data.config.tableNum;
		}
	}
}

/**
 * Get the order payment preference. We check all payment types and return the one that has been used with the localized data.
 *
 * @param {Order} order
 * @param {RestaurantExtraPaymentMethod[] | null} extraPaymentMethods
 * @return {string | undefined}
 */
export function getOrderPaymentPreference(order: Order, extraPaymentMethods: RestaurantExtraPaymentMethod[] | null): string | undefined {
	if(order.type === 'generic-catering') {
		const i18nPathPaymentPreferences: string = 'orders.management.order_modal.payment_preferences';
		if(order.data.checkout?.invoice) {
			const invoiceMethod = extraPaymentMethods?.find(method => method.type === 'invoice');
			const invoiceLabel = invoiceMethod ? Vue.filter('localize')(invoiceMethod, 'label') : i18n.t('orders.management.order_modal.payment_preferences.invoice');
			return invoiceLabel || i18n.t(`${i18nPathPaymentPreferences}.invoice`);
		}
		else if(order.data.checkout?.costCenter) {
			const costCenterMethod = extraPaymentMethods?.find(method => method.type === 'cost_center');
			const costCenterLabel = costCenterMethod ? Vue.filter('localize')(costCenterMethod, 'label') : i18n.t('orders.management.order_modal.payment_preferences.cost_center');
			return `${costCenterLabel}: (#${order.data.checkout.costCenter})` || `${i18n.t(`${i18nPathPaymentPreferences}.cost_center`)}: ${order.data.checkout.costCenter}`;
		}
		else if(order.data.checkout?.purchaseOrder) {
			const purchaseOrderMethod = extraPaymentMethods?.find(method => method.type === 'purchase_order');
			const purchaseOrderLabel = purchaseOrderMethod ? Vue.filter('localize')(purchaseOrderMethod, 'label') : i18n.t('orders.management.order_modal.payment_preferences.purchase_order');
			return `${purchaseOrderLabel}: (#${order.data.checkout.purchaseOrder})` || `${i18n.t(`${i18nPathPaymentPreferences}.purchase_order`)}: ${order.data.checkout.purchaseOrder}`;
		}
		return i18n.t(`${i18nPathPaymentPreferences}.credit_card`);
	}
	return undefined;
}

/**
 * Get the image URL from the item
 *
 * @param {string} itemImage
 * @return {string}
 */
export function getImageURL(itemImage: string): string {
	return process.env.NODE_ENV === 'development' ? `${itemImage}` : `${process.env.VUE_APP_S3_BUCKET_BASE_URL}/${itemImage}`;
}

/**
 * Get localized tax name
 * Special case for Quebec where PST is QST
 *
 * @param {string} taxName
 * @param {Restaurant} restaurant
 * @return {string}
 */
export function getLocalizedTaxName(taxName: string, restaurant: Restaurant): string {
	if (taxName === 'PST' && restaurant.address?.match(/, (QC|Québec|Quebec)/)) {
		return i18n.t('shared.taxes.qst');
	}
	return i18n.te(`shared.taxes.${taxName.toLowerCase()}`) ? i18n.t(`shared.taxes.${taxName.toLowerCase()}`) : taxName;
}

/**
 * Returns a list of the restaurant's default taxes as a string
 *
 * @param restaurant
 * @return {string}
 */
export function getDefaultTaxesApplied(restaurant: Restaurant): string {
	return restaurant.preset_tax_rates
		.filter((taxRate: PresetTaxes) => taxRate.is_default) // Filter by is_default
		.map((taxRate: PresetTaxes) => getLocalizedTaxName(taxRate.name, restaurant)) // Map to localized names
		.join(', '); // Join the names with a comma
}

/**
 * Format the answer of a custom question.
 * If it's a timepicker question, we format the date.
 * Answers without a question_type (legacy) are treated as timepicker only if they're a full ISO date-time.
 * Else we localize the answer.
 *
 * @param {AnsweredCustomQuestion} answeredQuestion
 * @return {string}
 */
export function formatCustomQuestionAnswer(answeredQuestion: AnsweredCustomQuestion): string {
	const isTimepicker = answeredQuestion.question_type
		? answeredQuestion.question_type === 'timepicker'
		: answeredQuestion.answer?.includes('T') && DateTime.fromISO(answeredQuestion.answer).isValid;

	if (isTimepicker) {
		return Vue.filter('formatDate')(answeredQuestion.answer, i18n.t('orders.guest_details.takeout.timepicker_format'));
	}
	return Vue.filter('localize')(answeredQuestion, 'answer');
}

/**
 * Get the effective price for an item or option, using adjustedPrice if available,
 * otherwise falling back to the regular price. (Subsidy or member pricing)
 *
 * @param {OrderItem | Option} item
 * @return {number}
 */
export function getEffectivePrice(item: OrderItem | Option): number {
	return item.adjustedPrice ? +item.adjustedPrice : +(item.price || 0);
}

/**
 * Get the order number to display, prefixed with the restaurant's internal identifier
 * when one is set (e.g. identifier "A" + order 73 -> "A73")
 *
 * @param {number | string} orderId
 * @param {string} [restaurantId]
 * @return {string}
 */
export function getDisplayOrderNumber(orderId: number | string, restaurantId?: string): string {
	// A restaurantId differing from the current restaurant means a marketplace order: look up the identifier in the locations list
	const identifier: string | null | undefined = !restaurantId || restaurantId === authState.restaurant.id
		? authState.restaurant.internal_identifier
		: authState.restaurants?.find((tenant: Tenant) => tenant.id === restaurantId)?.internal_identifier;

	return `${identifier || ''}${orderId}`;
}
